import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useTableSort } from '../hooks/useTableSort';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { formatDateTime } from '../utils/format';
import ErrorLoading from '../components/ErrorLoading';
import AutoRefreshControls from '../components/AutoRefreshControls';

const SOURCES = [
  { key: 'long_time', permission: '/tasks/long-time', endpoint: '/sg_pools/tasks/long_time_tasks', labelKey: 'tasks.tabLongTime' },
  { key: 'short_time', permission: '/tasks/short-time', endpoint: '/sg_pools/tasks/short_time_tasks', labelKey: 'tasks.tabShortTime' },
  { key: 'now_goal', permission: '/tasks/now-goal', endpoint: '/now_goal/tasks/now_goal_tasks', labelKey: 'tasks.tabNowGoal' },
];

const statusBadge = (status) => {
  const map = {
    pending: 'bg-warning text-dark',
    running: 'bg-primary',
    finished: 'bg-success',
    error: 'bg-danger',
    timeout: 'bg-secondary',
  };
  return <span className={`badge ${map[status] || 'bg-secondary'}`}>{status}</span>;
};

function Tasks({ token, backendUrl, userRoles, visiblePages }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isRootOrAdmin = userRoles?.includes('root') || userRoles?.includes('admin');
  const availableSources = useMemo(
    () => SOURCES.filter((s) => isRootOrAdmin || (visiblePages || []).includes(s.permission)),
    [isRootOrAdmin, visiblePages],
  );

  const requestedSource = searchParams.get('source');
  const activeSource = useMemo(() => {
    if (availableSources.find((s) => s.key === requestedSource)) return requestedSource;
    return availableSources[0]?.key;
  }, [requestedSource, availableSources]);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { sortableHeader, sortData } = useTableSort('created_at', 'desc');

  const activeSourceConfig = SOURCES.find((s) => s.key === activeSource);

  const fetchTasks = useCallback(async (signal) => {
    if (!activeSourceConfig || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${backendUrl}${activeSourceConfig.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      if (signal?.aborted) return;
      setTasks(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      setError(err.response?.data?.detail || err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [backendUrl, token, activeSourceConfig]);

  useEffect(() => {
    if (!token || !activeSourceConfig) return undefined;
    const controller = new AbortController();
    fetchTasks(controller.signal);
    return () => controller.abort();
  }, [token, activeSourceConfig, fetchTasks]);

  const autoRefresh = useAutoRefresh(fetchTasks, { defaultInterval: 60, defaultEnabled: true });

  const handleTabClick = (key) => {
    if (key === activeSource) return;
    setSearchParams({ source: key }, { replace: false });
    setSearchQuery('');
    setTasks([]);
    autoRefresh.setLastUpdated(null);
  };

  const filteredTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let filtered;
    if (activeSource === 'now_goal') {
      filtered = !q
        ? tasks
        : tasks.filter((it) =>
            String(it.league_name ?? '').toLowerCase().includes(q) ||
            String(it.home_team ?? '').toLowerCase().includes(q) ||
            String(it.away_team ?? '').toLowerCase().includes(q) ||
            String(it.url ?? '').toLowerCase().includes(q),
          );
    } else {
      filtered = !q
        ? tasks
        : tasks.filter((it) =>
            String(it.date_event_id ?? '').toLowerCase().includes(q) ||
            String(it.name ?? '').toLowerCase().includes(q),
          );
    }
    const sortKeys = activeSource === 'now_goal'
      ? ['created_at', 'last_check']
      : ['created_at', 'updated_at', 'last_check', 'start_time'];
    return sortData(filtered, sortKeys);
  }, [tasks, searchQuery, sortData, activeSource]);

  const renderSgPoolsTable = () => (
    <table className="table table-striped table-bordered table-hover">
      <thead>
        <tr>
          {sortableHeader(t('sgPools.eventId'), 'date_event_id')}
          {sortableHeader(t('sgPools.name'), 'name')}
          {sortableHeader(t('sgPools.startTime'), 'start_time')}
          {sortableHeader(t('common.status'), 'status')}
          {sortableHeader(t('tasks.lastCheck'), 'last_check')}
          {sortableHeader(t('common.createdAt'), 'created_at')}
          {sortableHeader(t('common.updatedAt'), 'updated_at')}
        </tr>
      </thead>
      <tbody>
        {filteredTasks.length === 0 && (
          <tr>
            <td colSpan={7} className="text-center text-muted">{t('common.noData')}</td>
          </tr>
        )}
        {filteredTasks.map((item) => (
          <tr key={item.date_event_id}>
            <td>
              <Link to={`/events/${item.date_event_id}`} target="_blank" rel="noreferrer">
                {item.date_event_id}
              </Link>
            </td>
            <td><a href={item.url}>{item.name}</a></td>
            <td>{formatDateTime(item.start_time)}</td>
            <td>{item.status}</td>
            <td>{formatDateTime(item.last_check)}</td>
            <td>{formatDateTime(item.created_at)}</td>
            <td>{formatDateTime(item.updated_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderNowGoalTable = () => (
    <table className="table table-striped table-bordered table-hover">
      <thead>
        <tr>
          {sortableHeader(t('tasks.league'), 'league_name')}
          {sortableHeader(t('nowGoal.homeTeam'), 'home_team')}
          {sortableHeader(t('nowGoal.awayTeam'), 'away_team')}
          {sortableHeader(t('nowGoal.liveTime'), 'live_time')}
          {sortableHeader(t('common.status'), 'status')}
          <th>{t('tasks.link')}</th>
          {sortableHeader(t('tasks.lastCheck'), 'last_check')}
          {sortableHeader(t('common.createdAt'), 'created_at')}
        </tr>
      </thead>
      <tbody>
        {filteredTasks.length === 0 && (
          <tr>
            <td colSpan={8} className="text-center text-muted">{t('common.noData')}</td>
          </tr>
        )}
        {filteredTasks.map((item, idx) => (
          <tr key={item.url || idx}>
            <td>{item.league_name}</td>
            <td>{item.home_team}</td>
            <td>{item.away_team}</td>
            <td>{item.live_time}</td>
            <td>{statusBadge(item.status)}</td>
            <td>
              <a href={item.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary" aria-label={item.url}>
                <i className="bi bi-box-arrow-up-right" aria-hidden="true"></i>
              </a>
            </td>
            <td>{formatDateTime(item.last_check)}</td>
            <td>{formatDateTime(item.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (availableSources.length === 0) {
    return (
      <div className="container py-4">
        <h2>{t('tasks.title')}</h2>
        <div className="alert alert-warning">{t('common.noData')}</div>
      </div>
    );
  }

  const searchPlaceholder = activeSource === 'now_goal' ? t('tasks.searchNowGoal') : t('tasks.searchPlaceholder');

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{t('tasks.title')}</h2>
        <div className="d-flex align-items-center gap-3">
          <span className="badge bg-info">{tasks.length} {t('common.items')}</span>
          <AutoRefreshControls {...autoRefresh} />
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        {availableSources.map((s) => (
          <li className="nav-item" key={s.key}>
            <button
              type="button"
              className={`nav-link ${activeSource === s.key ? 'active' : ''}`}
              onClick={() => handleTabClick(s.key)}
            >
              {t(s.labelKey)}
            </button>
          </li>
        ))}
      </ul>

      <div className="row g-2 align-items-center mb-3">
        <div className="col-auto">
          <label htmlFor="task-search" className="form-label">{t('common.search')}</label>
          <input
            id="task-search"
            className="form-control"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <ErrorLoading error={error} loading={loading} />

      {!loading && (
        <div className="table-responsive">
          {activeSource === 'now_goal' ? renderNowGoalTable() : renderSgPoolsTable()}
        </div>
      )}
    </div>
  );
}

export default Tasks;
